"use client"

import { useEffect, useState } from "react"
import { X } from "lucide-react"

interface LiquidGlassNotificationProps {
  message: string
  variant?: "success" | "error" | "warning" | "info"
  duration?: number
  onClose?: () => void
}

export function LiquidGlassNotification({
  message,
  variant = "success",
  duration = 5000,
  onClose,
}: LiquidGlassNotificationProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10)

    // Auto-hide after duration
    const timer = setTimeout(() => {
      handleClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [duration])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => {
      setIsVisible(false)
      onClose?.()
    }, 600)
  }

  const variantStyles = {
    success: {
      background: "linear-gradient(135deg, rgba(34, 197, 94, 0.85), rgba(22, 163, 74, 0.9))",
      shadow: "0 8px 32px rgba(34, 197, 94, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    },
    error: {
      background: "linear-gradient(135deg, rgba(239, 68, 68, 0.85), rgba(220, 38, 38, 0.9))",
      shadow: "0 8px 32px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    },
    warning: {
      background: "linear-gradient(135deg, rgba(251, 146, 60, 0.85), rgba(249, 115, 22, 0.9))",
      shadow: "0 8px 32px rgba(251, 146, 60, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    },
    info: {
      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.85), rgba(37, 99, 235, 0.9))",
      shadow: "0 8px 32px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    },
  }

  const style = variantStyles[variant]

  if (!isVisible && !isExiting) return null

  return (
    <div
      className="fixed top-20 left-1/2 z-[9999] pointer-events-auto"
      style={{
        transform: isVisible && !isExiting 
          ? "translateX(-50%) translateY(0)" 
          : "translateX(-50%) translateY(-120%)",
        transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        className="relative px-6 py-4 rounded-3xl border border-white/20 backdrop-blur-xl min-w-[320px] max-w-[500px]"
        style={{
          background: style.background,
          boxShadow: style.shadow,
          animation: isVisible && !isExiting ? "liquidRipple 0.6s ease-out" : "none",
        }}
      >
        {/* Glass refraction effect */}
        <div
          className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none"
          style={{
            background: "radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 60%)",
            animation: isVisible && !isExiting ? "glassRefraction 0.4s ease-out 0.2s" : "none",
          }}
        />

        {/* Content */}
        <div className="relative flex items-center justify-between gap-4">
          <p
            className="text-white font-medium text-sm leading-relaxed"
            style={{
              animation: isVisible && !isExiting ? "textFadeIn 0.5s ease-out 0.3s both" : "none",
            }}
          >
            {message}
          </p>
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
            aria-label="Close notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Liquid morph effect */}
        <div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)",
            animation: isVisible && !isExiting ? "liquidMorph 0.8s ease-out" : "none",
          }}
        />
      </div>

      <style jsx>{`
        @keyframes liquidRipple {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes glassRefraction {
          0% {
            opacity: 0;
            transform: scale(0.5);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes textFadeIn {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes liquidMorph {
          0% {
            transform: scale(0);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.3;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  )
}

// Hook for managing notifications
export function useLiquidNotification() {
  const [notification, setNotification] = useState<{
    message: string
    variant: "success" | "error" | "warning" | "info"
  } | null>(null)

  const showNotification = (
    message: string,
    variant: "success" | "error" | "warning" | "info" = "success"
  ) => {
    setNotification({ message, variant })
  }

  const hideNotification = () => {
    setNotification(null)
  }

  return {
    notification,
    showNotification,
    hideNotification,
  }
}
